/**
 * PeopleFiles Component
 * 
 * Displays files uploaded/shared by a specific person.
 * Reuses file management components but with filtered data for the person.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Button, Form, Dropdown, Badge, Table } from 'react-bootstrap';
import Select from 'react-select';
import FilterProjectFilesOffcanvas from '../../../pages/Projects/Detail/files/ProjectFilesFilter';
import { UploadFileModal, FilePreviewModal, RenameFileModal, MoveFileModal } from '../../../pages/Projects/Detail/files/ProjectFilesModals';
import '../../../pages/Projects/Detail/files/ProjectFiles.css';

const PeopleFiles = () => {
    const { peopleId } = useParams();
    
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [files, setFiles] = useState([]);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);

    // Upload Form State
    const [uploadFile, setUploadFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [fileCategory, setFileCategory] = useState(null);
    const [fileDescription, setFileDescription] = useState('');

    // Filter State
    const [filterKeywords, setFilterKeywords] = useState('');
    const [filterCategories, setFilterCategories] = useState(null);
    const [filterUploadedBy, setFilterUploadedBy] = useState(null);
    const [filterStartDate, setFilterStartDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);

    // Mock data
    const categoryOptions = [
        { value: 'design', label: 'Design', color: '#8b5cf6', icon: 'fa-palette' },
        { value: 'development', label: 'Development', color: '#3b82f6', icon: 'fa-code' },
        { value: 'documents', label: 'Documents', color: '#10b981', icon: 'fa-file-alt' },
        { value: 'assets', label: 'Assets', color: '#f59e0b', icon: 'fa-images' },
        { value: 'other', label: 'Other', color: '#6b7280', icon: 'fa-folder' }
    ];

    const mockFiles = [
        { id: 1, name: 'Homepage Mockup.fig', size: '2.4 MB', type: 'figma', category: 'design', uploadedBy: 'Sarah Wilson', uploadedDate: '2024-12-01', description: 'Final homepage design mockup with all sections' },
        { id: 2, name: 'API Documentation.pdf', size: '1.8 MB', type: 'pdf', category: 'documents', uploadedBy: 'John Doe', uploadedDate: '2024-11-30', description: 'Complete API documentation for backend services' },
        { id: 3, name: 'Logo Design.psd', size: '15.2 MB', type: 'psd', category: 'design', uploadedBy: 'Sarah Wilson', uploadedDate: '2024-11-28', description: 'Brand logo design with variations' },
        { id: 4, name: 'User Flow Diagram.png', size: '892 KB', type: 'image', category: 'design', uploadedBy: 'Mike Johnson', uploadedDate: '2024-11-25', description: 'Complete user journey flow diagram' },
        { id: 5, name: 'Database Schema.sql', size: '45 KB', type: 'sql', category: 'development', uploadedBy: 'John Doe', uploadedDate: '2024-11-24', description: 'Database schema and migration scripts' },
        { id: 6, name: 'Project Proposal.docx', size: '2.1 MB', type: 'docx', category: 'documents', uploadedBy: 'David Brown', uploadedDate: '2024-11-22', description: 'Initial project proposal and scope document' }
    ];

    useEffect(() => {
        setFiles(mockFiles);
        // NOTE: In real implementation, filter files by peopleId
        console.log('Loading files for person:', peopleId);
    }, [peopleId]);

    const getFileIcon = (fileType) => {
        const icons = {
            'figma': { icon: 'fab fa-figma', color: '#F24E1E' },
            'sketch': { icon: 'fas fa-pencil-ruler', color: '#F7B500' },
            'pdf': { icon: 'fas fa-file-pdf', color: '#dc2626' },
            'docx': { icon: 'fas fa-file-word', color: '#2563eb' },
            'xlsx': { icon: 'fas fa-file-excel', color: '#16a34a' },
            'pptx': { icon: 'fas fa-file-powerpoint', color: '#f59e0b' },
            'zip': { icon: 'fas fa-file-archive', color: '#f59e0b' },
            'code': { icon: 'fas fa-file-code', color: '#8b5cf6' },
            'sql': { icon: 'fas fa-database', color: '#f59e0b' },
            'psd': { icon: 'fas fa-file-image', color: '#2563eb' },
            'svg': { icon: 'fas fa-file-image', color: '#06b6d4' },
            'video': { icon: 'fas fa-file-video', color: '#ec4899' },
            'image': { icon: 'fas fa-file-image', color: '#10b981' },
            'audio': { icon: 'fas fa-file-audio', color: '#16a34a' }
        };
        return icons[fileType] || { icon: 'fas fa-file', color: '#6b7280' };
    };

    const getCategoryColor = (category) => {
        const cat = categoryOptions.find(c => c.value === category);
        return cat ? cat.color : '#6b7280';
    };

    const getCategoryIcon = (category) => {
        const cat = categoryOptions.find(c => c.value === category);
        return cat ? cat.icon : 'fa-folder';
    };

    const handleFileSelect = (fileId) => {
        setSelectedFiles(prev => 
            prev.includes(fileId) 
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );
    };

    const handleSelectAll = () => {
        if (selectedFiles.length === filteredFiles.length) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(filteredFiles.map(f => f.id));
        }
    };

    const handleFileClick = (file) => {
        setSelectedFile(file);
        setShowPreviewModal(true);
    };

    const handleRename = (file) => {
        setSelectedFile(file);
        setFileName(file.name);
        setShowRenameModal(true);
    };

    const handleMove = () => {
        setShowMoveModal(true);
    };

    const handleDelete = (fileIds) => {
        console.log('Delete files:', fileIds);
        setFiles(prev => prev.filter(f => !fileIds.includes(f.id)));
        setSelectedFiles([]);
    };

    const handleUploadFile = () => {
        // TODO: Implement file upload API call
        console.log('Uploading file:', { uploadFile, fileName, fileCategory, fileDescription });
        setShowUploadModal(false);
        resetUploadForm();
    };

    const resetUploadForm = () => {
        setUploadFile(null);
        setFileName('');
        setFileCategory(null);
        setFileDescription('');
    };

    // Calculations for files display
    const getFilteredFiles = () => {
        const filesToFilter = files.length > 0 ? files : mockFiles;
        return filesToFilter.filter(file => {
            const matchesCategory = selectedCategory === 'all' || file.category === selectedCategory;
            const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (file.description && file.description.toLowerCase().includes(searchQuery.toLowerCase()));
            return matchesCategory && matchesSearch;
        });
    };

    const filteredFiles = getFilteredFiles();
    const filesToUse = files.length > 0 ? files : mockFiles;
    const totalFiles = filesToUse.length;
    const totalSize = filesToUse.reduce((acc, file) => {
        const size = parseFloat(file.size);
        const unit = file.size.includes('MB') ? 1 : file.size.includes('KB') ? 0.001 : 0.000001;
        return acc + (size * unit);
    }, 0).toFixed(1);

    const categoryCounts = categoryOptions.map(cat => ({
        ...cat,
        count: filesToUse.filter(f => f.category === cat.value).length
    }));

    const lastUploadedFile = filesToUse[0];

    // Pagination
    const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedFiles = filteredFiles.slice(startIndex, startIndex + itemsPerPage);

    return (
        <>
            {/* File Summary Stats */}
            <Row className="g-4 mb-4">
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 file-stat-card-hover">
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0 rounded-3 icon-box-48">
                                <i className="fas fa-folder-open"></i>
                            </div>
                            <div className="flex-grow-1">
                                <div className="text-primary fw-bold mb-1" style={{ fontSize: '20px', lineHeight: '1' }}>{totalFiles}</div>
                                <div className="text-secondary small">Total Files</div>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 file-stat-card-hover">
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0 rounded-3 icon-box-48">
                                <i className="fas fa-database"></i>
                            </div>
                            <div className="flex-grow-1">
                                <div className="text-success fw-bold mb-1" style={{ fontSize: '20px', lineHeight: '1' }}>{totalSize} MB</div>
                                <div className="text-secondary small">Total Size</div>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 file-stat-card-hover">
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning flex-shrink-0 rounded-3 icon-box-48">
                                <i className="fas fa-clock"></i>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                                <div className="text-warning fw-bold mb-1 text-truncate" style={{ fontSize: '14px', lineHeight: '1.2' }}>{lastUploadedFile?.name || 'N/A'}</div>
                                <div className="text-secondary small">Last Uploaded</div>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 file-stat-card-hover">
                        <div>
                            <div className="text-secondary small mb-2">Files by Category</div>
                            <div className="d-flex flex-wrap gap-2">
                                {categoryCounts.slice(0, 3).map(cat => (
                                    <Badge
                                        key={cat.value}
                                        bg="light"
                                        text="dark"
                                        className="border category-badge-sm"
                                        style={{ borderColor: cat.color, fontSize: '0.7rem' }}
                                    >
                                        {cat.label}: {cat.count}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Main Files Section */}
            <div className="c-card-1">
                <div className="c-card-header-1">
                    <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
                        {/* Left Section - Title */}
                        <div>
                            <div className="mb-0 fw-semibold">Files</div>
                        </div>

                        {/* Right Section - Controls */}
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {/* Category Filter */}
                            <Dropdown className='c-dropdown-1'>
                                <Dropdown.Toggle variant='link' className='border rounded-1 px-3 py-2 bg-transparent text-decoration-none'>
                                    <i className="fas fa-layer-group me-2"></i>
                                    {selectedCategory === 'all' ? 'All Categories' : categoryOptions.find(c => c.value === selectedCategory)?.label}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => setSelectedCategory('all')}
                                        active={selectedCategory === 'all'}
                                    >
                                        <i className="fas fa-folder me-2"></i>
                                        All Categories
                                    </Dropdown.Item>
                                    {categoryOptions.map(cat => (
                                        <Dropdown.Item
                                            key={cat.value}
                                            onClick={() => setSelectedCategory(cat.value)}
                                            active={selectedCategory === cat.value}
                                        >
                                            <i className={`fas ${cat.icon} me-2`} style={{ color: cat.color }}></i>
                                            {cat.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Search Box */}
                            <div className="position-relative">
                                <div className="files-search-wrapper d-flex align-items-center">
                                    <i className="fas fa-search files-search-icon" aria-hidden="true"></i>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search files..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="files-search-input"
                                        aria-label="Search files"
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            className="files-search-clear"
                                            onClick={() => setSearchQuery('')}
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <i className="fas fa-times" aria-hidden="true"></i>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* View Mode Toggle */}
                            <div className="files-view-toggle d-flex gap-0 p-1">
                                <Button
                                    variant={viewMode === 'grid' ? 'primary' : 'light'}
                                    className={`files-view-btn d-flex align-items-center justify-content-center p-0 ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                    title="Grid View"
                                >
                                    <i className="fas fa-th"></i>
                                </Button>
                                <Button
                                    variant={viewMode === 'list' ? 'primary' : 'light'}
                                    className={`files-view-btn d-flex align-items-center justify-content-center p-0 ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => setViewMode('list')}
                                    title="List View"
                                >
                                    <i className="fas fa-list"></i>
                                </Button>
                            </div>

                            {/* Advanced Filter Button */}
                            <Button
                                variant="outline-primary"
                                onClick={() => setShowFilterOffcanvas(true)}
                                title="Advanced Filters"
                            >
                                <i className="fas fa-filter me-2"></i>
                                Filters
                            </Button>

                            {/* Upload Button */}
                            <Button
                                variant="primary"
                                className="files-upload-btn text-nowrap"
                                onClick={() => setShowUploadModal(true)}
                            >
                                <i className="fas fa-cloud-upload-alt me-2"></i>
                                Upload
                            </Button>
                        </div> 
                    </div>
                </div>

                <div className={`c-card-body-1 ${viewMode === 'list' ? ' p-0' : ''}`}>
                    {filteredFiles.length === 0 ? (
                        <div className="files-empty-state text-center py-5 px-4">
                            <i className="fas fa-folder-open mb-4"></i>
                            <h5 className="mb-2 fs-18">No Files Found</h5>
                            <p className="text-muted mb-3 fs-14">
                                {searchQuery || selectedCategory !== 'all'
                                    ? 'Try adjusting your filters or search query'
                                    : 'Upload your first file to get started'}
                            </p>
                            {!searchQuery && selectedCategory === 'all' && (
                                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                                    <i className="fas fa-cloud-upload-alt me-2"></i>Upload File
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Grid View */}
                            {viewMode === 'grid' && (
                        <Row className="g-3">
                            {paginatedFiles.map(file => {
                                const fileIcon = getFileIcon(file.type);
                                return (
                                    <Col key={file.id} xs={6} sm={4} md={3} lg={2}>
                                        <div
                                            className="premium-file-card"
                                            onClick={() => handleFileClick(file)}
                                        >
                                            {/* Action Dropdown - Top Right */}
                                            <div className="premium-file-action">
                                                <Dropdown className='c-dropdown-1' onClick={(e) => e.stopPropagation()}>
                                                    <Dropdown.Toggle variant="link">
                                                        <i className="fas fa-ellipsis-v"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu align="end">
                                                        <Dropdown.Item onClick={() => handleFileClick(file)}>
                                                            <i className="fas fa-eye me-2"></i> Preview
                                                        </Dropdown.Item>
                                                        <Dropdown.Item>
                                                            <i className="fas fa-download me-2"></i> Download
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleRename(file)}>
                                                            <i className="fas fa-edit me-2"></i> Rename
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleMove()}>
                                                            <i className="fas fa-folder me-2"></i> Move
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleDelete([file.id])}>
                                                            <i className="fas fa-trash me-2"></i> Delete
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>

                                            {/* File Icon Section with Badge */}
                                            <div className="premium-file-icon-section d-flex flex-column align-items-center p-3">
                                                <div
                                                    className="premium-file-icon-wrapper d-flex align-items-center justify-content-center"
                                                    style={{
                                                        backgroundColor: `${fileIcon.color}10`,
                                                        borderColor: `${fileIcon.color}25`
                                                    }}
                                                >
                                                    <i
                                                        className={`fas ${getCategoryIcon(file.category)}`}
                                                        style={{ color: fileIcon.color, fontSize: '1.7rem' }}
                                                    ></i>
                                                </div>
                                                <div className='file-name fs-14 mt-3'> {categoryOptions.find(c => c.value === file.category)?.label} </div>
                                            </div>

                                            {/* File Content Section */}
                                            <div className="premium-file-content p-3">
                                                <h6 className="premium-file-name fs-14" title={file.name}>
                                                    {file.name}
                                                </h6>

                                                {/* File Meta Info Row */}
                                                <div className="premium-file-meta fs-12 d-flex align-items-center gap-2">
                                                    <span className="premium-file-size">
                                                        {file.size}
                                                    </span>
                                                    <span className="premium-file-divider">•</span>
                                                    <span className="premium-file-date">
                                                        {file.uploadedDate}
                                                    </span>
                                                </div>

                                                {/* File Footer - Uploader with User Icon */}
                                                <div className="premium-file-footer mt-2 pt-2">
                                                    <div className="premium-file-uploader fs-14 d-flex align-items-center gap-1">
                                                        <span>{file.uploadedBy}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                )
                            })}
                        </Row>
                            )}

                            {/* List View */}
                            {viewMode === 'list' && (
                        <Table responsive className='c-table'>
                            <thead>
                                <tr>
                                    <th width="50px" className='text-center'>
                                        <Form.Check />
                                    </th>
                                    <th>File Name</th>
                                    <th width="150px" className='text-center'>Category</th>
                                    <th width="100px" className='text-center'>Size</th>
                                    <th width="150px" className='text-center'>Uploaded By</th>
                                    <th width="120px" className='text-center'>Date</th>
                                    <th width="80px" className='text-center'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedFiles.map(file => {
                                    const fileIcon = getFileIcon(file.type);
                                    return (
                                        <tr key={file.id} style={{ cursor: 'pointer' }} onClick={() => handleFileClick(file)}>
                                            <td className='text-center'>
                                                <Form.Check 
                                                    checked={selectedFiles.includes(file.id)}
                                                    onChange={() => handleFileSelect(file.id)}
                                                    onClick={(e) => e.stopPropagation()} 
                                                />
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <i
                                                        className={`${fileIcon.icon} me-2`}
                                                        style={{ color: fileIcon.color, fontSize: '1.25rem' }}
                                                    ></i>
                                                    <span className="fw-medium">{file.name}</span>
                                                </div>
                                            </td>
                                            <td className='text-center text-muted'> {categoryOptions.find(c => c.value === file.category)?.label} </td> 
                                            <td className='text-center text-muted'>{file.size}</td>
                                            <td className='text-center text-muted'>{file.uploadedBy}</td>
                                            <td className='text-center text-muted'>{file.uploadedDate}</td>
                                            <td className='text-center'>
                                                <Dropdown className='c-dropdown-1' onClick={(e) => e.stopPropagation()}>
                                                    <Dropdown.Toggle variant="link" id="dropdown-basic">
                                                        <i className="fas fa-ellipsis-v"></i>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => handleFileClick(file)}>
                                                            <i className="fas fa-eye me-2"></i> Preview
                                                        </Dropdown.Item>
                                                        <Dropdown.Item>
                                                            <i className="fas fa-download me-2"></i> Download
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleRename(file)}>
                                                            <i className="fas fa-edit me-2"></i> Rename
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleMove()}>
                                                            <i className="fas fa-folder me-2"></i> Move
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleDelete([file.id])}>
                                                            <i className="fas fa-trash me-2"></i> Delete
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                            )}
                        </>
                    )}

                    {/* Pagination */}
                    {filteredFiles.length > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredFiles.length)} of {filteredFiles.length} files
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <UploadFileModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                fileName={fileName}
                setFileName={setFileName}
                fileCategory={fileCategory}
                setFileCategory={setFileCategory}
                fileDescription={fileDescription}
                setFileDescription={setFileDescription}
                categoryOptions={categoryOptions}
                onSubmit={handleUploadFile}
            />

            {/* Filter Offcanvas */}
            <FilterProjectFilesOffcanvas
                show={showFilterOffcanvas}
                onHide={() => setShowFilterOffcanvas(false)}
                filterKeywords={filterKeywords}
                setFilterKeywords={setFilterKeywords}
                filterCategories={filterCategories}
                setFilterCategories={setFilterCategories}
                filterUploadedBy={filterUploadedBy}
                setFilterUploadedBy={setFilterUploadedBy}
                filterStartDate={filterStartDate}
                setFilterStartDate={setFilterStartDate}
                filterEndDate={filterEndDate}
                setFilterEndDate={setFilterEndDate}
                categoryOptions={categoryOptions}
            />

            {/* Other File Modals */}
            <FilePreviewModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                file={selectedFile}
                getFileIcon={getFileIcon}
                getCategoryColor={getCategoryColor}
            />

            <RenameFileModal
                show={showRenameModal}
                onHide={() => setShowRenameModal(false)}
                file={selectedFile}
                fileName={fileName}
                setFileName={setFileName}
            />

            <MoveFileModal
                show={showMoveModal}
                onHide={() => setShowMoveModal(false)}
                selectedFiles={selectedFiles}
                allFiles={files.length > 0 ? files : mockFiles}
            />
        </>
    );
};

export default PeopleFiles;
